import { useState, useEffect, useCallback } from 'react';
import { Save, RefreshCw, Gauge, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default content - same as in PhidelLevel.js
const defaultContent = {
  en: {
    title: "Understanding Your Level",
    intro: "Your Level shows your current playing level based on your match results.",
    understandingTitle: "What is Level?",
    understanding: [
      "Level is calculated from your performance against other players.",
      "It updates after every match.",
      "It reflects how you actually play, not how you rate yourself."
    ],
    calculationTitle: "How is it calculated?",
    calculationIntro: "We use a dynamic rating system based on:",
    calculation: ["Who you play against", "Whether you win or lose", "How close or dominant the match was", "How certain we already are about your level"],
    calculationNotes: ["Beating stronger players increases your level more.", "Losing to weaker players decreases it more.", "Expected results (strong vs weak) have smaller impact."],
    certaintyTitle: "What is Certainty?",
    certaintyIntro: "Certainty shows how confident the system is about your level.",
    certainty: ["More matches = higher certainty", "Less matches = lower certainty", "Higher certainty = smaller level changes"],
    certaintyLevelsTitle: "Certainty Levels",
    certaintyLevels: [
      { level: "Very Low", meaning: "Not enough data yet" },
      { level: "Low", meaning: "Early estimate" },
      { level: "Medium", meaning: "Becoming reliable" },
      { level: "High", meaning: "Strong estimate" },
      { level: "Very High", meaning: "Very stable level" }
    ],
    matchesTitle: "How many matches do I need?",
    matches: ["5-10 matches: first meaningful level", "10-20 matches: good accuracy", "25+ matches: stable level"],
    levelMeaningTitle: "Level Meaning",
    levelMeaning: [
      { range: "< 3.5", category: "Beginner" },
      { range: "3.5 - 4.5", category: "Low Intermediate" },
      { range: "4.5 - 5.5", category: "Intermediate" },
      { range: "5.5 - 6.5", category: "Advanced" },
      { range: "6.5+", category: "Competitive" }
    ],
    faqTitle: "Frequently Asked Questions",
    whyTitle: "Why this system?",
    why: ["Reflect real performance", "Encourage fair matches", "Avoid inflated self-ratings", "Create a balanced competitive environment"],
    footer: "Your level is not a label. It's a snapshot of your current performance. Play, improve, and let the system adjust."
  },
  el: {
    title: "Τι σημαίνει το Level σου;",
    intro: "Το Level δείχνει πώς παίζεις αυτή τη στιγμή, με βάση τα αποτελέσματα των αγώνων σου.",
    understandingTitle: "Τι είναι το Level;",
    understanding: ["Υπολογίζεται από την απόδοσή σου απέναντι σε άλλους παίκτες", "Ενημερώνεται μετά από κάθε αγώνα", "Δείχνει το πώς πραγματικά παίζεις, όχι το πώς νομίζεις ότι παίζεις"],
    calculationTitle: "Πώς λειτουργεί;",
    calculationIntro: "Το σύστημα βασίζεται:",
    calculation: ["Με ποιους παίζεις", "Αν κερδίζεις ή χάνεις", "Πόσο κοντινό ή καθαρό ήταν το αποτέλεσμα", "Πόσο σίγουρο είναι ήδη το σύστημα για το Level σου"],
    calculationNotes: ["Αν κερδίσεις πιο δυνατούς παίκτες, το Level σου ανεβαίνει περισσότερο", "Αν χάσεις από πιο αδύναμους, πέφτει περισσότερο", "Τα αναμενόμενα αποτελέσματα επηρεάζουν λιγότερο"],
    certaintyTitle: "Τι είναι το Certainty;",
    certaintyIntro: "Certainty = Βεβαιότητα. Το Certainty δείχνει πόσο σίγουρο είναι το σύστημα για το Level σου.",
    certainty: ["Περισσότεροι αγώνες = μεγαλύτερο Certainty", "Λιγότεροι αγώνες = μικρότερο Certainty", "Μεγαλύτερο Certainty = μικρότερες αλλαγές στο Level"],
    certaintyLevelsTitle: "Certainty Levels",
    certaintyLevels: [
      { level: "Very Low", meaning: "Δεν υπάρχουν αρκετά δεδομένα" },
      { level: "Low", meaning: "Αρχική εκτίμηση" },
      { level: "Medium", meaning: "Αρχίζει να γίνεται αξιόπιστο" },
      { level: "High", meaning: "Αρκετά σίγουρη εικόνα" },
      { level: "Very High", meaning: "Πολύ σταθερό Level" }
    ],
    matchesTitle: "Πόσους αγώνες χρειάζεσαι;",
    matches: ["5-10: παίρνεις μια πρώτη εικόνα", "10-20: το Level γίνεται αξιόπιστο", "25+: το Level σταθεροποιείται"],
    levelMeaningTitle: "Τι σημαίνουν τα Levels;",
    levelMeaning: [
      { range: "< 3.5", category: "Beginner" },
      { range: "3.5 - 4.5", category: "Low Intermediate" },
      { range: "4.5 - 5.5", category: "Intermediate" },
      { range: "5.5 - 6.5", category: "Advanced" },
      { range: "6.5+", category: "Competitive" }
    ],
    faqTitle: "Συχνές Ερωτήσεις",
    whyTitle: "Γιατί υπάρχει αυτό το σύστημα;",
    why: ["Δείχνει την πραγματική απόδοση", "Βοηθά να γίνονται πιο ισορροπημένα παιχνίδια", "Αποφεύγονται φουσκωμένα Levels", "Δημιουργείται πιο δίκαιο ανταγωνιστικό περιβάλλον"],
    footer: "Το Level σου δεν είναι ταμπέλα. Είναι μια εικόνα του πώς παίζεις τώρα. Παίξε, βελτιώσου και άσε το σύστημα να σε ακολουθήσει."
  }
};

export default function AdminPhidelLevelEditor() {
  const { t, language } = useLanguage();
  const { token } = useAuth();
  const [content, setContent] = useState(defaultContent);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState('en');

  const fetchContent = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/phidel-level-content`);
      if (res.ok) {
        const data = await res.json();
        setContent({
          en: { ...defaultContent.en, ...(data.en || {}) },
          el: { ...defaultContent.el, ...(data.el || {}) }
        });
      }
    } catch (error) {
      console.error('Failed to fetch content:', error);
      setContent(defaultContent);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/phidel-level-content`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(content)
      });

      if (res.ok) {
        toast.success(language === 'el' ? 'Το περιεχόμενο αποθηκεύτηκε' : 'Content saved');
        setHasChanges(false);
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error(t('error'));
    } finally {
      setSaving(false);
    }
  };

  const updateField = (lang, field, value) => {
    setContent(prev => ({
      ...prev,
      [lang]: { ...prev[lang], [field]: value }
    }));
    setHasChanges(true);
  };

  const updateArrayItem = (lang, field, index, value) => {
    setContent(prev => ({
      ...prev,
      [lang]: {
        ...prev[lang],
        [field]: prev[lang][field].map((item, i) => i === index ? value : item)
      }
    }));
    setHasChanges(true);
  };

  const addArrayItem = (lang, field) => {
    setContent(prev => ({
      ...prev,
      [lang]: { ...prev[lang], [field]: [...prev[lang][field], ''] }
    }));
    setHasChanges(true);
  };

  const removeArrayItem = (lang, field, index) => {
    setContent(prev => ({
      ...prev,
      [lang]: {
        ...prev[lang],
        [field]: prev[lang][field].filter((_, i) => i !== index)
      }
    }));
    setHasChanges(true);
  };

  // For table items (certaintyLevels, levelMeaning)
  const updateTableItem = (lang, field, index, key, value) => {
    setContent(prev => ({
      ...prev,
      [lang]: {
        ...prev[lang],
        [field]: prev[lang][field].map((item, i) => 
          i === index ? { ...item, [key]: value } : item
        )
      }
    }));
    setHasChanges(true);
  };

  const addTableItem = (lang, field, template) => {
    setContent(prev => ({
      ...prev,
      [lang]: { ...prev[lang], [field]: [...(prev[lang][field] || []), template] }
    }));
    setHasChanges(true);
  };

  const removeTableItem = (lang, field, index) => {
    setContent(prev => ({
      ...prev,
      [lang]: {
        ...prev[lang],
        [field]: prev[lang][field].filter((_, i) => i !== index)
      }
    }));
    setHasChanges(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="phidel-level-editor-loading">
        <div className="animate-spin h-8 w-8 border-4 border-muted border-t-primary rounded-full" />
      </div>
    );
  }

  const renderArraySection = (lang, fieldName, labelTitle, labelItems) => {
    const c = content[lang];
    return (
      <div className="border border-border rounded-lg p-4">
        <div className="mb-3">
          <Label>{labelTitle}</Label>
          <Input
            value={c[fieldName + 'Title'] || ''}
            onChange={(e) => updateField(lang, fieldName + 'Title', e.target.value)}
            className="mt-1"
          />
        </div>
        {c[fieldName] && (
          <>
            <Label className="text-sm text-muted-foreground">{labelItems}</Label>
            <div className="space-y-2 mt-2">
              {c[fieldName].map((item, idx) => (
                <div key={idx} className="flex gap-2">
                  <Input
                    value={item}
                    onChange={(e) => updateArrayItem(lang, fieldName, idx, e.target.value)}
                    className="flex-1"
                  />
                  <Button variant="ghost" size="icon" onClick={() => removeArrayItem(lang, fieldName, idx)} className="text-red-500">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => addArrayItem(lang, fieldName)} className="gap-1">
                <Plus className="h-3 w-3" /> Add
              </Button>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderCertaintyLevelsTable = (lang) => {
    const c = content[lang];
    const isEn = lang === 'en';
    const items = c.certaintyLevels || [];
    
    return (
      <div className="border border-border rounded-lg p-4">
        <div className="mb-3">
          <Label>{isEn ? 'Certainty Levels Title' : 'Τίτλος Επιπέδων Βεβαιότητας'}</Label>
          <Input
            value={c.certaintyLevelsTitle || ''}
            onChange={(e) => updateField(lang, 'certaintyLevelsTitle', e.target.value)}
            className="mt-1"
          />
        </div>
        <Label className="text-sm text-muted-foreground">{isEn ? 'Certainty Levels Table' : 'Πίνακας Επιπέδων Βεβαιότητας'}</Label>
        <div className="mt-2 space-y-2">
          <div className="grid grid-cols-[1fr_2fr_auto] gap-2 text-xs font-medium text-muted-foreground">
            <span>{isEn ? 'Level' : 'Επίπεδο'}</span>
            <span>{isEn ? 'Meaning' : 'Σημασία'}</span>
            <span></span>
          </div>
          {items.map((item, idx) => (
            <div key={idx} className="grid grid-cols-[1fr_2fr_auto] gap-2">
              <Input
                value={item.level || ''}
                onChange={(e) => updateTableItem(lang, 'certaintyLevels', idx, 'level', e.target.value)}
                placeholder={isEn ? 'Level' : 'Επίπεδο'}
              />
              <Input
                value={item.meaning || ''}
                onChange={(e) => updateTableItem(lang, 'certaintyLevels', idx, 'meaning', e.target.value)}
                placeholder={isEn ? 'Meaning' : 'Σημασία'}
              />
              <Button variant="ghost" size="icon" onClick={() => removeTableItem(lang, 'certaintyLevels', idx)} className="text-red-500">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => addTableItem(lang, 'certaintyLevels', { level: '', meaning: '' })} className="gap-1">
            <Plus className="h-3 w-3" /> {isEn ? 'Add Row' : 'Προσθήκη'}
          </Button>
        </div>
      </div>
    );
  };

  const renderLevelMeaningTable = (lang) => {
    const c = content[lang];
    const isEn = lang === 'en';
    const items = c.levelMeaning || [];
    
    return (
      <div className="border border-border rounded-lg p-4">
        <div className="mb-3">
          <Label>{isEn ? 'Level Meaning Title' : 'Τίτλος Σημασίας Level'}</Label>
          <Input
            value={c.levelMeaningTitle || ''}
            onChange={(e) => updateField(lang, 'levelMeaningTitle', e.target.value)}
            className="mt-1"
          />
        </div>
        <Label className="text-sm text-muted-foreground">{isEn ? 'Level Meaning Table' : 'Πίνακας Σημασίας Level'}</Label>
        <div className="mt-2 space-y-2">
          <div className="grid grid-cols-[1fr_2fr_auto] gap-2 text-xs font-medium text-muted-foreground">
            <span>{isEn ? 'Range' : 'Εύρος'}</span>
            <span>{isEn ? 'Category' : 'Κατηγορία'}</span>
            <span></span>
          </div>
          {items.map((item, idx) => (
            <div key={idx} className="grid grid-cols-[1fr_2fr_auto] gap-2">
              <Input
                value={item.range || ''}
                onChange={(e) => updateTableItem(lang, 'levelMeaning', idx, 'range', e.target.value)}
                placeholder={isEn ? 'Range' : 'Εύρος'}
              />
              <Input
                value={item.category || ''}
                onChange={(e) => updateTableItem(lang, 'levelMeaning', idx, 'category', e.target.value)}
                placeholder={isEn ? 'Category' : 'Κατηγορία'}
              />
              <Button variant="ghost" size="icon" onClick={() => removeTableItem(lang, 'levelMeaning', idx)} className="text-red-500">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => addTableItem(lang, 'levelMeaning', { range: '', category: '' })} className="gap-1">
            <Plus className="h-3 w-3" /> {isEn ? 'Add Row' : 'Προσθήκη'}
          </Button>
        </div>
      </div>
    );
  };

  const renderLanguageEditor = (lang) => {
    const c = content[lang];
    const isEn = lang === 'en';

    return (
      <div className="space-y-6">
        {/* Title & Intro */}
        <div className="space-y-4">
          <div>
            <Label>{isEn ? 'Page Title' : 'Τίτλος Σελίδας'}</Label>
            <Input value={c.title} onChange={(e) => updateField(lang, 'title', e.target.value)} className="mt-1" />
          </div>
          <div>
            <Label>{isEn ? 'Introduction' : 'Εισαγωγή'}</Label>
            <Textarea value={c.intro} onChange={(e) => updateField(lang, 'intro', e.target.value)} rows={2} className="mt-1" />
          </div>
        </div>

        {/* Understanding Section */}
        {renderArraySection(lang, 'understanding', isEn ? 'Understanding Title' : 'Τίτλος Κατανόησης', isEn ? 'Understanding Points' : 'Σημεία Κατανόησης')}

        {/* Calculation Section */}
        <div className="border border-border rounded-lg p-4">
          <div className="mb-3">
            <Label>{isEn ? 'Calculation Title' : 'Τίτλος Υπολογισμού'}</Label>
            <Input value={c.calculationTitle} onChange={(e) => updateField(lang, 'calculationTitle', e.target.value)} className="mt-1" />
          </div>
          <div className="mb-3">
            <Label>{isEn ? 'Calculation Intro' : 'Εισαγωγή Υπολογισμού'}</Label>
            <Input value={c.calculationIntro} onChange={(e) => updateField(lang, 'calculationIntro', e.target.value)} className="mt-1" />
          </div>
          <Label className="text-sm text-muted-foreground">{isEn ? 'Calculation Points' : 'Σημεία Υπολογισμού'}</Label>
          <div className="space-y-2 mt-2">
            {c.calculation.map((item, idx) => (
              <div key={idx} className="flex gap-2">
                <span className="flex items-center justify-center w-6 h-9 text-sm font-medium text-muted-foreground">{idx + 1}.</span>
                <Input value={item} onChange={(e) => updateArrayItem(lang, 'calculation', idx, e.target.value)} className="flex-1" />
                <Button variant="ghost" size="icon" onClick={() => removeArrayItem(lang, 'calculation', idx)} className="text-red-500">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => addArrayItem(lang, 'calculation')} className="gap-1">
              <Plus className="h-3 w-3" /> Add
            </Button>
          </div>
          <div className="mt-4">
            <Label className="text-sm text-muted-foreground">{isEn ? 'Important Notes' : 'Σημαντικές Σημειώσεις'}</Label>
            <div className="space-y-2 mt-2">
              {c.calculationNotes.map((item, idx) => (
                <div key={idx} className="flex gap-2">
                  <Input value={item} onChange={(e) => updateArrayItem(lang, 'calculationNotes', idx, e.target.value)} className="flex-1" />
                  <Button variant="ghost" size="icon" onClick={() => removeArrayItem(lang, 'calculationNotes', idx)} className="text-red-500">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => addArrayItem(lang, 'calculationNotes')} className="gap-1">
                <Plus className="h-3 w-3" /> Add
              </Button>
            </div>
          </div>
        </div>

        {/* Certainty Section */}
        <div className="border border-border rounded-lg p-4">
          <div className="mb-3">
            <Label>{isEn ? 'Certainty Title' : 'Τίτλος Βεβαιότητας'}</Label>
            <Input value={c.certaintyTitle} onChange={(e) => updateField(lang, 'certaintyTitle', e.target.value)} className="mt-1" />
          </div>
          <div className="mb-3">
            <Label>{isEn ? 'Certainty Intro' : 'Εισαγωγή Βεβαιότητας'}</Label>
            <Textarea value={c.certaintyIntro} onChange={(e) => updateField(lang, 'certaintyIntro', e.target.value)} rows={2} className="mt-1" />
          </div>
          <Label className="text-sm text-muted-foreground">{isEn ? 'Certainty Points' : 'Σημεία Βεβαιότητας'}</Label>
          <div className="space-y-2 mt-2">
            {c.certainty.map((item, idx) => (
              <div key={idx} className="flex gap-2">
                <Input value={item} onChange={(e) => updateArrayItem(lang, 'certainty', idx, e.target.value)} className="flex-1" />
                <Button variant="ghost" size="icon" onClick={() => removeArrayItem(lang, 'certainty', idx)} className="text-red-500">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => addArrayItem(lang, 'certainty')} className="gap-1">
              <Plus className="h-3 w-3" /> Add
            </Button>
          </div>
        </div>

        {/* Certainty Levels Table */}
        {renderCertaintyLevelsTable(lang)}

        {/* Level Meaning Table */}
        {renderLevelMeaningTable(lang)}

        {/* Matches Section */}
        {renderArraySection(lang, 'matches', isEn ? 'Matches Needed Title' : 'Τίτλος Απαιτούμενων Αγώνων', isEn ? 'Matches Points' : 'Σημεία Αγώνων')}

        {/* Why Section */}
        {renderArraySection(lang, 'why', isEn ? 'Why This System Title' : 'Τίτλος Γιατί το Σύστημα', isEn ? 'Why Points' : 'Σημεία Γιατί')}

        {/* Footer */}
        <div>
          <Label>{isEn ? 'Footer Quote' : 'Φράση Footer'}</Label>
          <Textarea value={c.footer} onChange={(e) => updateField(lang, 'footer', e.target.value)} rows={2} className="mt-1" />
        </div>
      </div>
    );
  };

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-4xl mx-auto" data-testid="admin-phidel-level-editor-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
            <Gauge className="h-5 w-5 text-blue-500" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground">
              {language === 'el' ? 'Επεξεργαστής Phidél Level' : 'Phidél Level Editor'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {language === 'el' ? 'Επεξεργασία περιεχομένου για κάθε γλώσσα' : 'Edit content for each language'}
            </p>
          </div>
        </div>
        
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchContent} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={handleSave} disabled={saving || !hasChanges} size="sm" data-testid="save-phidel-level-btn">
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : t('save')}
          </Button>
        </div>
      </div>

      {/* Unsaved changes warning */}
      {hasChanges && (
        <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-600 dark:text-amber-400 text-sm">
          {language === 'el' ? 'Έχετε μη αποθηκευμένες αλλαγές.' : 'You have unsaved changes.'}
        </div>
      )}

      {/* Language Tabs */}
      <div className="bg-card rounded-xl border border-border p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="en" className="gap-2">
              <span className="inline-flex items-center justify-center w-6 h-5 rounded bg-green-500/20 text-green-600 dark:text-green-400 text-xs font-bold">EN</span>
              English
            </TabsTrigger>
            <TabsTrigger value="el" className="gap-2">
              <span className="inline-flex items-center justify-center w-6 h-5 rounded bg-blue-500/20 text-blue-600 dark:text-blue-400 text-xs font-bold">EL</span>
              Ελληνικά
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="en">{renderLanguageEditor('en')}</TabsContent>
          <TabsContent value="el">{renderLanguageEditor('el')}</TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
